import { useDisclosure } from '@chakra-ui/react'
import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import ReferralModal, { ReferralModalView } from './index'

interface ReferralModalContextType {
  isOpen: boolean
  openReferralModal: (view?: ReferralModalView) => void
  closeReferralModal: () => void
}

const ReferralModalContext = createContext<
  ReferralModalContextType | undefined
>(undefined)

export const useReferralModalGlobal = (): ReferralModalContextType => {
  const context = useContext(ReferralModalContext)
  if (!context) {
    throw new Error(
      'useReferralModalGlobal must be used within a ReferralModalProvider'
    )
  }
  return context
}

interface ReferralModalProviderProps {
  children: ReactNode
}

const REFERRAL_MODAL_VIEWED_KEY = 'growsurf_referral_modal_viewed'

export const ReferralModalProvider: React.FC<ReferralModalProviderProps> = ({
  children
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentView, setCurrentView] = useState<
    ReferralModalView | undefined
  >()
  const [hasViewedBefore, setHasViewedBefore] = useState<boolean>(false)

  useEffect(() => {
    const hasViewed = localStorage.getItem(REFERRAL_MODAL_VIEWED_KEY)
    setHasViewedBefore(!!hasViewed)
  }, [])

  const openReferralModal = (view?: ReferralModalView) => {
    if (!hasViewedBefore) {
      setCurrentView(ReferralModalView.RewardTiers)
      localStorage.setItem(REFERRAL_MODAL_VIEWED_KEY, 'true')
      setHasViewedBefore(true)
    } else {
      setCurrentView(view)
    }

    onOpen()
  }

  return (
    <ReferralModalContext.Provider
      value={{
        isOpen,
        openReferralModal,
        closeReferralModal: () => {
          setCurrentView(undefined)
          onClose()
        }
      }}
    >
      {children}
      <ReferralModal
        isOpen={isOpen}
        onClose={onClose}
        initialView={currentView}
      />
    </ReferralModalContext.Provider>
  )
}
